import type { RegisterSWOptions } from 'virtual:pwa-register';

/**
 * SW 更新通道（R-06 ②、契约 §25、`05-ui-design-system.md` §5.6）。
 *
 * ## 为什么注册与「比对」分成两个模块
 *
 * `virtual:pwa-register` 是 `vite-plugin-pwa` 提供的**虚拟模块**：它只在 Vite 构建期存在，
 * jsdom 里根本解析不了。而 `apps/tenant/src` 里的**任何** spec 只要（直接或间接）import 到
 * 静态引用它的模块，整个文件都会在收集阶段报「Failed to resolve import」—— 收银端外壳被六个
 * spec 引用，等于把整套收银端用例一起打掉。
 *
 * ⇒ 做法是**把 `registerSW` 的调用点收成一个注入参数**：
 *   · 真调用点在 `main.tsx`（`import { registerSW } from 'virtual:pwa-register'`），
 *     那是**唯一**的静态引用处，spec 从不 import 它；
 *   · 本模块只声明**结构类型** `SwRegisterFn`（type-only import，编译后不留运行时依赖），
 *     spec 传假实现即可驱动 `onNeedRefresh` / `onRegisteredSW` 两条回调。
 *
 * ⚠️ **不许为了好测就把 `virtual:pwa-register` 从实现里删掉**：`vite.config.ts` 用的是
 * `injectRegister: 'auto'`，而它的语义正是「应用自己 import 了虚拟模块就**不再**自动注入
 * `registerSW.js`」⇒ 删掉真调用点等于真产物**没有 SW 注册**（PWA 装到桌面也永远停在旧版）。
 *
 * ## 为什么用 window 事件而不是模块级订阅表
 *
 * 注册发生在 `main.tsx`（React 之外），而提示由 `useSwUpdate` 渲染（React 之内）。
 * 用事件把两者接起来，状态就只活在 React 那一侧 ⇒ **用例之间零串扰**：
 * 模块级的 `updateAvailable` 一旦被某个用例置真，后面每个用例都得靠一个 reset 钩子洗掉，
 * 而那种钩子本身没人测。
 */

/** 更新可用的事件名（`main.tsx` 的注册回调与 `useSwUpdate` 之间**唯一**的接口） */
export const SW_UPDATE_EVENT = 'cuito:sw-update-available';

/** `virtual:pwa-register` 的 `registerSW` 的结构类型（type-only，不留运行时依赖） */
export type SwRegisterFn = (options?: RegisterSWOptions) => (reloadPage?: boolean) => Promise<void>;

/**
 * 「让新版本生效」的那一次动作。
 *
 * `onRegisteredSW` 给到 registration 时优先用它（`reg.update()` 会真的去查一次新 SW，
 * 让「用户点了刷新、但 waiting worker 还没就位」也有一条前进的路）。
 * 没拿到 registration（离线注册、或插件没回调）时退回 `registerSW` 返回的 `updateSW(true)`。
 */
let applyUpdate: (() => void) | null = null;

/** `onNeedRefresh` 的落点：广播给所有已挂载的 `useSwUpdate` */
export function markSwUpdateAvailable(): void {
  window.dispatchEvent(new Event(SW_UPDATE_EVENT));
}

/**
 * 启动时注册 SW 并挂上两条回调（`main.tsx` 调用，**全应用一处**）。
 *
 * 三个触发点里的两个在这里落地：① SW 进入 `waiting` ⇒ `onNeedRefresh` ⇒ 广播；
 * ② `onRegisteredSW` ⇒ 记下 registration 供「刷新」按钮使用。
 * 第三个（启动时的**版本比对**）在 `useSwUpdate` 里，因为它要 React 状态去渲染提示。
 */
export function startSwUpdate(register: SwRegisterFn): void {
  const updateSW = register({
    immediate: true,
    onNeedRefresh(): void {
      markSwUpdateAvailable();
    },
    onRegisteredSW(_swScriptUrl, registration): void {
      if (registration === undefined) return;
      applyUpdate = (): void => {
        void registration.update();
      };
    },
  });
  if (applyUpdate === null) {
    applyUpdate = (): void => {
      void updateSW(true);
    };
  }
}

/**
 * 点「刷新」之后走的路（§5.6 冻结的三步：`SKIP_WAITING` → `controllerchange` → `location.reload`）。
 *
 * `skipWaiting` 用**既有 02-architecture 的协议字面量** `{ type: 'SKIP_WAITING' }`，
 * 收银端与 SW 两侧逐字一致；`registration.waiting` 为空时 postMessage 是 no-op
 * （那种情况是「服务端版本变了但浏览器还没发现新 SW」，重载本身就会重新取一次 HTML）。
 */
export function reloadToNewVersion(): void {
  applyUpdate?.();
  if ('serviceWorker' in navigator) {
    void navigator.serviceWorker.ready.then((registration) => {
      registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
    });
  }
  window.location.reload();
}
